import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "..";
import type {
  AnnualIncomeBreakdownData,
  AnnualIncomeBreakdownReqParams,
  FaaTrackingParams,
  FaaTrackingResponse,
  HatGarTrackingParams,
  HatGarTrackingResponse,
  LeadershipTripQualParams,
  LeadershipTripQualResponse,
} from "./annualIncomeDashboardAPI.types";
import type { LSPathViewResponse } from "../LeadershipSeminar/leadershipSeminarAPI.types";

export const annualIncomeDashboardAPI = createApi({
  reducerPath: "annualIncomeDashboardAPI",
  baseQuery: dynamicBaseQuery,
  keepUnusedDataFor: 3600,
  endpoints: (builder) => ({
    getHatGarTracking: builder.query<HatGarTrackingResponse, HatGarTrackingParams>({
      query: (params) => {
        const { aff, abo, period, isoCountryCode, locale } = params;
        const queryParams = new URLSearchParams({
          Aff: aff,
          Abo: abo,
          Period: period,
          IsoCountryCode: isoCountryCode,
          Locale: locale,
        });

        return `/hat-gar-tracking?${queryParams}`;
      },
      extraOptions: { pathKey: "annualDash" },
    }),

    getFaaTracking: builder.query<FaaTrackingResponse, FaaTrackingParams>({
      query: (params) => {
        const { aff, abo, period, isoCountryCode, locale } = params;
        const queryParams = new URLSearchParams({
          Aff: aff,
          Abo: abo,
          Period: period,
          IsoCountryCode: isoCountryCode,
          Locale: locale,
        });

        return `/faa-tracking?${queryParams}`;
      },
      extraOptions: { pathKey: "annualDash" },
    }),

    getLeadershipTripQual: builder.query<LeadershipTripQualResponse, LeadershipTripQualParams>({
      query: (params) => {
        const { abo, aff, locale, period, isoCountryCode, isoCurrencyCode } = params;
        const queryParams = new URLSearchParams({
          Abo: abo,
          Aff: aff,
          Locale: locale,
          Period: period,
          IsoCountryCode: isoCountryCode,
          IsoCurrencyCode: isoCurrencyCode,
        });

        return `/incentives-v2/api/leadership-trip-qual?${queryParams}`;
      },
    }),

    getLeadershipSeminarPathView: builder.query<LSPathViewResponse, LeadershipTripQualParams>({
      query: (params) => {
        const { abo, aff, locale, period, isoCountryCode, isoCurrencyCode } = params;
        const queryParams = new URLSearchParams({
          Aff: aff,
          Abo: abo,
          Period: period,
          IsoCountryCode: isoCountryCode,
          IsoCurrencyCode: isoCurrencyCode,
          Locale: locale,
        });

        return `/incentives-v2/api/lsi/pathview?${queryParams}`;
      },
    }),

    getAnnualIncomeBreakdown: builder.query<AnnualIncomeBreakdownData, AnnualIncomeBreakdownReqParams>({
      query: (params) => {
        const queryParams = new URLSearchParams({
          Locale: params.locale,
          Period: params.period,
          Abo: params.abo,
          Aff: params.aff,
          IsoCountryCode: params.isoCountryCode,
        });

        return `/annual-income-breakdown?${queryParams}`;
      },
      extraOptions: { pathKey: "annualDash" },
    }),
  }),
});

export const {
  useGetHatGarTrackingQuery,
  useGetFaaTrackingQuery,
  useGetLeadershipTripQualQuery,
  useGetLeadershipSeminarPathViewQuery,
  useGetAnnualIncomeBreakdownQuery,
} = annualIncomeDashboardAPI;
